import { Array, BigDecimal, Match, Predicate, Schema } from 'effect';

import type {
  ListingSelection,
  ListingSort,
  RangeSelection,
  TermSelection,
} from '../../capability/productListing/listingSelection';
import {
  canonicalSelection,
  emptySelection,
} from '../../capability/productListing/listingSelection';
import {
  canonicalTokens,
  FilterValueToken,
  type FilterToken,
  type FilterTokens,
} from '../url/filterTokens';
import {
  entryForCode,
  entryForKey,
  tokenForEntry,
  type ListingVocabulary,
  type VocabularyEntry,
} from './ListingVocabulary';

/** Scales exact decimal bounds without binary floating-point drift. */
const exactScaledBound = (value: number, scale: number): number =>
  BigDecimal.toNumberUnsafe(
    BigDecimal.multiply(BigDecimal.fromNumberUnsafe(value), BigDecimal.fromNumberUnsafe(scale))
  );

/** A value that an open terms entry passes to the URL unchanged. */
const isFilterValueToken = Schema.is(FilterValueToken);

const termEntryForCode = (
  vocabulary: ListingVocabulary,
  code: string
): Extract<VocabularyEntry, { readonly _tag: 'Terms' }> | undefined => {
  const entry = entryForCode(vocabulary, code);
  return entry !== undefined && Predicate.isTagged('Terms')(entry) ? entry : undefined;
};

const rangeEntryForCode = (
  vocabulary: ListingVocabulary,
  code: string
): Extract<VocabularyEntry, { readonly _tag: 'Range' }> | undefined => {
  const entry = entryForCode(vocabulary, code);
  return entry !== undefined && Predicate.isTagged('Range')(entry) ? entry : undefined;
};

const vocabularyValue = (
  entry: Extract<VocabularyEntry, { readonly _tag: 'Terms' }>,
  token: string
): string | undefined => {
  if (entry.values.length === 0) return isFilterValueToken(token) ? token : undefined;
  return entry.values.find((value) => value.token === token)?.value;
};

/** Translates canonical URL tokens into an engine selection. */
export const toSelection = (
  tokens: FilterTokens,
  vocabulary: ListingVocabulary
): {
  readonly selection: ListingSelection;
  readonly page: number;
  readonly unknown: ReadonlyArray<string>;
} => {
  const terms: TermSelection[] = [];
  const ranges: RangeSelection[] = [];
  const unknown: string[] = [];
  const normalized = canonicalTokens(tokens);

  for (const token of normalized.filters) {
    Match.value(token).pipe(
      Match.tag('Terms', ({ code, values }) => {
        const entry = termEntryForCode(vocabulary, code);
        if (entry === undefined) {
          unknown.push(code);
          return;
        }
        const translated = values.flatMap((value) => {
          const result = vocabularyValue(entry, value);
          if (result === undefined) unknown.push(`${code}:${value}`);
          return result === undefined ? [] : [result];
        });
        if (Array.isReadonlyArrayNonEmpty(translated)) {
          terms.push({ key: entry.key, values: translated });
        }
      }),
      Match.tag('Range', ({ code, min, max }) => {
        const entry = rangeEntryForCode(vocabulary, code);
        if (entry === undefined) {
          unknown.push(code);
          return;
        }
        const translated: RangeSelection = {
          key: entry.key,
          min:
            min === null
              ? null
              : entry.rounding === 'exact'
                ? exactScaledBound(min, entry.scale)
                : min * entry.scale,
          max:
            max === null
              ? null
              : entry.rounding === 'exact'
                ? exactScaledBound(max, entry.scale)
                : max * entry.scale,
        };
        ranges.push({
          key: translated.key,
          min: translated.min === entry.defaultBounds?.min ? null : translated.min,
          max: translated.max === entry.defaultBounds?.max ? null : translated.max,
        });
      }),
      Match.exhaustive
    );
  }

  let sort: ListingSort = emptySelection.sort;
  if (normalized.sort !== null) {
    const vocabularySort = vocabulary.sorts.find((entry) => entry.token === normalized.sort);
    if (vocabularySort === undefined) unknown.push(`sort:${normalized.sort}`);
    else sort = vocabularySort.sort;
  }

  return {
    selection: canonicalSelection({ terms, ranges, sort }),
    page: normalized.page,
    unknown,
  };
};

/** Translates an engine selection into canonical URL tokens. */
export const toTokens = (
  selection: ListingSelection,
  page: number,
  vocabulary: ListingVocabulary
): { readonly tokens: FilterTokens; readonly unrepresented: ReadonlyArray<string> } => {
  const filters: FilterToken[] = [];
  const unrepresented: string[] = [];
  const normalized = canonicalSelection(selection);

  for (const term of normalized.terms) {
    const entry = entryForKey(vocabulary, term.key);
    if (entry === undefined || !Predicate.isTagged('Terms')(entry)) {
      unrepresented.push(`term:${JSON.stringify(term.key)}`);
      continue;
    }
    const values = term.values.flatMap((value) => {
      const token = tokenForEntry(entry, value);
      if (token === undefined) unrepresented.push(`${entry.code}:${value}`);
      return token === undefined ? [] : [token];
    });
    if (Array.isReadonlyArrayNonEmpty(values)) {
      filters.push({ _tag: 'Terms', code: entry.code, values });
    }
  }

  for (const range of normalized.ranges) {
    const entry = entryForKey(vocabulary, range.key);
    if (entry === undefined || !Predicate.isTagged('Range')(entry) || entry.scale <= 0) {
      unrepresented.push(`range:${JSON.stringify(range.key)}`);
      continue;
    }
    filters.push({
      _tag: 'Range',
      code: entry.code,
      min:
        range.min === null || range.min === entry.defaultBounds?.min
          ? null
          : entry.rounding === 'exact' || (entry.rounding === undefined && entry.scale === 1)
            ? range.min / entry.scale
            : Math.floor(range.min / entry.scale),
      max:
        range.max === null || range.max === entry.defaultBounds?.max
          ? null
          : entry.rounding === 'exact' || (entry.rounding === undefined && entry.scale === 1)
            ? range.max / entry.scale
            : Math.ceil(range.max / entry.scale),
    });
  }

  const sort =
    normalized.sort.field === 'none'
      ? null
      : (vocabulary.sorts.find(
          (entry) =>
            entry.sort.field === normalized.sort.field &&
            entry.sort.direction === normalized.sort.direction
        )?.token ?? null);
  if (normalized.sort.field !== 'none' && sort === null) unrepresented.push('sort');

  return { tokens: canonicalTokens({ filters, sort, page: Math.max(1, page) }), unrepresented };
};
